import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  Chip,
  Alert
} from '@mui/material';
import { MatchData } from '@/interfaces/liveGamesType';
import ReactApexChart from 'react-apexcharts';
import ReactDOMServer from 'react-dom/server';
import { useAuth } from '@/context/AuthContext';
import { isMatchLive } from '../../utils';
import * as S from './styles';
import { ApexOptions } from 'apexcharts';
import useLiveMatchComments, { convertSecondsToTime } from '@/hooks/fetchHooks/useLiveMatchComments';

interface AttackMomentumV2Props {
  match: MatchData | null;
}

const AttackMomentumV2: React.FC<AttackMomentumV2Props> = ({ match }) => {
  const { isAuthenticated } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fallbackData, setFallbackData] = useState<number[]>([]);
  const fallbackDataGenerated = useRef(false);

  // Verificar se a partida está ao vivo
  const matchIsLive = match ? isMatchLive(match) : false;
  const matchId = match?.match_id;
  const isValidMatchId = !!matchId && matchId !== 'undefined' && matchId !== 'null';

  // Log para depuração - apenas uma vez quando o componente monta ou o match muda
  useEffect(() => {
    if (match) {
      console.log('Match data:', match);
      console.log('Match ID:', matchId);
      console.log('Match is live:', matchIsLive);
      console.log('Is valid match ID:', isValidMatchId);
    }
  }, [match]);

  // Usar o hook personalizado para buscar e processar os comentários ao vivo
  const {
    formattedTime,
    liveOddsData,
    isLoading,
    isError,
    error
  } = useLiveMatchComments(matchId, isAuthenticated, {
    refetchInterval: matchIsLive ? 60000 : false, // Atualizar a cada 1 minuto se a partida estiver ao vivo
    enabled: isValidMatchId,
    meta: {
      errorMessage: "Ocorreu um erro ao buscar os dados do gráfico de posse de bola"
    }
  });

  // Log para depuração - apenas quando os dados mudam
  useEffect(() => {
    console.log('Live odds data:', liveOddsData);
    console.log('Formatted time data:', formattedTime);
    console.log('Is loading:', isLoading);
    console.log('Is error:', isError);
    if (isError) {
      console.error('Error details:', error);
    }
  }, [liveOddsData, formattedTime, isLoading, isError, error]);

  // Gerar dados de fallback se não houver dados reais - com verificação para evitar loop infinito
  useEffect(() => {
    // Apenas gerar dados de fallback se:
    // 1. Não estamos carregando
    // 2. Não temos dados formatados
    // 3. Não geramos dados de fallback anteriormente
    // 4. Temos um ID de partida válido
    if (
      !isLoading &&
      formattedTime.length === 0 &&
      !fallbackDataGenerated.current &&
      isValidMatchId
    ) {
      console.log('Gerando dados de fallback para exibição');
      // Gerar 10 valores aleatórios entre -30 e 30
      const mockData = Array.from({ length: 10 }, () =>
        Math.floor(Math.random() * 60) - 30
      );
      setFallbackData(mockData);
      // Marcar que já geramos dados de fallback para evitar gerar novamente
      fallbackDataGenerated.current = true;
    }
  }, [isLoading, formattedTime.length, isValidMatchId]);

  // Resetar o flag de fallback quando o matchId muda
  useEffect(() => {
    fallbackDataGenerated.current = false;
  }, [matchId]);

  // Verificar se temos dados válidos e definir mensagem de erro
  useEffect(() => {
    if (isError) {
      console.error('Erro ao buscar dados de momentum:', error);
      setErrorMessage('Ocorreu um erro ao buscar os dados do gráfico de posse de bola. Exibindo dados simulados.');
    } else if (formattedTime.length === 0 && !isLoading) {
      console.log('Sem dados de momentum disponíveis');
      if (matchIsLive) {
        setErrorMessage('Aguardando dados de momentum de ataque para esta partida ao vivo. Exibindo dados simulados.');
      } else {
        setErrorMessage('Não há dados de momentum de ataque disponíveis para esta partida. Exibindo dados simulados.');
      }
    } else {
      setErrorMessage(null);
    }
  }, [isError, error, formattedTime.length, isLoading, matchIsLive]);

  // Dados a serem usados (reais ou fallback)
  const dataToUse = useMemo(() => {
    // Se temos dados reais, usá-los
    if (formattedTime.length > 0) {
      return formattedTime;
    }

    // Em outros casos, usar dados de fallback
    return fallbackData;
  }, [formattedTime, fallbackData]);

  // Determinar se devemos mostrar o gráfico ou uma mensagem
  const shouldShowChart = useMemo(() => {
    // Não mostrar o gráfico se estamos carregando
    if (isLoading) return false;

    // Mostrar o gráfico se temos dados reais
    if (formattedTime.length > 0) return true;

    // Mostrar o gráfico com dados de fallback se temos dados de fallback
    if (fallbackData.length > 0) return true;

    // Em outros casos, não mostrar o gráfico
    return false;
  }, [isLoading, formattedTime.length, fallbackData.length]);

  // Determinar a mensagem a ser exibida quando não há gráfico
  const noDataMessage = useMemo(() => {
    if (isError) {
      return 'Ocorreu um erro ao buscar os dados. Exibindo dados simulados.';
    }

    if (matchIsLive) {
      return 'Aguardando dados de momentum para esta partida ao vivo. Exibindo dados simulados.';
    }

    return 'Não há dados de momentum de ataque disponíveis para esta partida. Exibindo dados simulados.';
  }, [isError, matchIsLive]);

  // Configuração do gráfico
  const chartOptions = useMemo<ApexOptions>(() => {
    return {
      chart: {
        type: 'bar' as const,
        height: 200,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        background: 'transparent'
      },
      plotOptions: {
        bar: {
          colors: {
            ranges: [
              {
                from: -100,
                to: -1,
                color: '#F15B46',
              },
            ],
          },
          columnWidth: '80%',
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 0
      },
      colors: ['#1976d2'],
      xaxis: {
        categories: Array.from({ length: dataToUse.length }, (_, i) => i.toString()),
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          show: false
        }
      },
      grid: {
        show: false
      },
      tooltip: {
        theme: 'dark',
        custom: function ({ series, seriesIndex, dataPointIndex }: any) {
          const value = series[seriesIndex][dataPointIndex];
          const isHome = value > 0;
          const teamName = isHome
            ? liveOddsData?.match_hometeam_name || match?.match_hometeam_name
            : liveOddsData?.match_awayteam_name || match?.match_awayteam_name;
          const color = isHome ? '#1976d2' : '#F15B46';
          const intensity = Math.abs(value);
          let actionType = 'Posse de bola';

          if (intensity >= 25) actionType = 'Finalização';
          else if (intensity >= 20) actionType = 'Ataque perigoso';
          else if (intensity >= 10) actionType = 'Ataque';
          else if (intensity >= 5) actionType = 'Posse de bola';

          return ReactDOMServer.renderToString(
            <S.TooltipContainer color={color}>
              <div className="indicator"></div>
              <div>
                <div>{teamName}</div>
                <div>{actionType} (Intensidade: {intensity})</div>
              </div>
            </S.TooltipContainer>
          );
        }
      }
    };
  }, [dataToUse, liveOddsData, match]);

  // Dados para o gráfico
  const chartSeries = useMemo(() => {
    return [
      {
        name: 'Momentum de Ataque',
        data: dataToUse
      }
    ];
  }, [dataToUse]);

  // Calcular porcentagens de posse
  const possessionStats = useMemo(() => {
    const homeCount = dataToUse.filter(v => v > 0).length;
    const awayCount = dataToUse.filter(v => v < 0).length;
    const totalCount = dataToUse.filter(v => v !== 0).length;

    if (totalCount === 0) {
      return { home: 50, away: 50 };
    }

    return {
      home: Math.round((homeCount / totalCount) * 100),
      away: Math.round((awayCount / totalCount) * 100)
    };
  }, [dataToUse]);

  if (!match) {
    return (
      <S.StatsNotFound>
        <Typography variant="body1">Selecione uma partida para ver o gráfico de posse de bola</Typography>
      </S.StatsNotFound>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={2} height="200px">
        <CircularProgress size={30} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Carregando dados de momentum de ataque...
        </Typography>
      </Box>
    );
  }

  // Se ocorreu um erro, mas temos dados de fallback, exibir o gráfico com os dados de fallback
  const showFallbackData = isError && fallbackData.length > 0;

  return (
    <S.GraphContainer>
      <S.GraphTitle>
        <Typography variant="subtitle2">Momentum de Ataque</Typography>
        {matchIsLive && (
          <S.LiveIndicator>
            <div className="dot"></div>
            <Chip
              label="AO VIVO"
              size="small"
              color="success"
              variant="outlined"
            />
          </S.LiveIndicator>
        )}
      </S.GraphTitle>

      {(errorMessage || showFallbackData) && (
        <Alert severity={showFallbackData ? "warning" : "info"} sx={{ mb: 2 }}>
          {errorMessage || noDataMessage}
        </Alert>
      )}

      <S.ChartWrapper>
        <S.ChartImages>
          <img
            src={match.team_home_badge}
            alt={match.match_hometeam_name}
            onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/40' }}
          />
          <img
            src={match.team_away_badge}
            alt={match.match_awayteam_name}
            onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/40' }}
          />
        </S.ChartImages>

        <S.ChartContainer>
          {shouldShowChart || showFallbackData ? (
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={200}
            />
          ) : (
            <S.StatsNotFound>
              <Typography variant="body2">{noDataMessage}</Typography>
            </S.StatsNotFound>
          )}
        </S.ChartContainer>
      </S.ChartWrapper>

      {(shouldShowChart || showFallbackData) && (
        <S.PossessionStats>
          <S.TeamPossession>
            <Typography variant="caption">{liveOddsData?.match_hometeam_name || match.match_hometeam_name}</Typography>
            <Typography className="percentage">
              {possessionStats.home}%
            </Typography>
          </S.TeamPossession>

          <S.TeamPossession>
            <Typography variant="caption">{liveOddsData?.match_awayteam_name || match.match_awayteam_name}</Typography>
            <Typography className="percentage">
              {possessionStats.away}%
            </Typography>
          </S.TeamPossession>
        </S.PossessionStats>
      )}
    </S.GraphContainer>
  );
};

export default AttackMomentumV2;